import { useState, useEffect } from "react";
import { RefreshCw } from "lucide-react";
import type { DataRecord } from "../types";
import { syncGeoTracking, fetchGeoTracking } from "../api";

interface GeoTrackingPanelProps {
  geoTracking?: {
    clientSlug?: string;
    hasData?: boolean;
    kpis?: DataRecord;
    kpisAb?: DataRecord;
    kpisOtras?: DataRecord;
    kpisGlobal?: DataRecord;
    asistentes?: DataRecord[];
    asistentesAb?: DataRecord[];
    runs?: DataRecord[];
    error?: string;
  };
  clientSlug?: string;
}

export function GeoTrackingPanel({ geoTracking, clientSlug }: GeoTrackingPanelProps) {
  const [activeGeo, setActiveGeo] = useState(geoTracking);
  const [showRuns, setShowRuns] = useState(false);
  const [runsFilter, setRunsFilter] = useState<"all" | "ab" | "other">("all");
  const [asistentesView, setAsistentesView] = useState<"ab" | "all">("ab");
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  useEffect(() => {
    setActiveGeo(geoTracking);
  }, [geoTracking]);

  const targetSlug = clientSlug || activeGeo?.clientSlug;

  const handleSync = async () => {
    setIsSyncing(true);
    setSyncMsg(null);
    try {
      const res = await syncGeoTracking();
      if (res.ok) {
        setSyncMsg(
          `Sincronizado OK: ${res.after.total} filas (+${res.filas_agregadas} nuevas)`
        );
        if (targetSlug) {
          const fresh = await fetchGeoTracking(targetSlug);
          setActiveGeo(fresh);
        }
      }
    } catch (err: any) {
      setSyncMsg(`Error al sincronizar: ${err.message || String(err)}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const isDriveError =
    activeGeo?.error?.includes("Drive credentials") ||
    activeGeo?.error?.includes("403");

  const kpisAb = activeGeo?.kpisAb || {};
  const kpisOtras = activeGeo?.kpisOtras || {};
  const kpisGlobal = activeGeo?.kpisGlobal || activeGeo?.kpis || {};
  const runs = activeGeo?.runs || [];

  const totalAb = Number(kpisAb.total_evaluaciones ?? 0);
  const mencionesAb = Number(kpisAb.menciones ?? 0);
  const pctMencionAb =
    kpisAb.pct_mencion != null
      ? Number(kpisAb.pct_mencion)
      : totalAb > 0
      ? Number(((mencionesAb / totalAb) * 100).toFixed(1))
      : 0;
  const avgRankAb = kpisAb.avg_rank != null ? Number(kpisAb.avg_rank) : null;
  const linksAb = Number(kpisAb.links_verificables ?? 0);
  const busqAb = Number(kpisAb.busquedas_activadas ?? 0);
  const pctBusqAb =
    totalAb > 0 ? ((busqAb / totalAb) * 100).toFixed(1) : "0";

  const totalOtras = Number(kpisOtras.total_evaluaciones ?? 0);
  const mencionesOtras = Number(kpisOtras.menciones ?? 0);
  const pctMencionOtras =
    kpisOtras.pct_mencion != null
      ? Number(kpisOtras.pct_mencion)
      : totalOtras > 0
      ? Number(((mencionesOtras / totalOtras) * 100).toFixed(1))
      : 0;
  const avgRankOtras =
    kpisOtras.avg_rank != null ? Number(kpisOtras.avg_rank) : null;
  const linksOtras = Number(kpisOtras.links_verificables ?? 0);

  const totalGlobal = Number(kpisGlobal.total_evaluaciones ?? 0);
  const mencionesGlobal = Number(kpisGlobal.menciones ?? 0);
  const pctMencionGlobal =
    kpisGlobal.pct_mencion != null
      ? Number(kpisGlobal.pct_mencion)
      : totalGlobal > 0
      ? Number(((mencionesGlobal / totalGlobal) * 100).toFixed(1))
      : 0;
  const avgRankGlobal =
    kpisGlobal.avg_rank != null ? Number(kpisGlobal.avg_rank) : null;
  const linksGlobal = Number(kpisGlobal.links_verificables ?? 0);
  const busqGlobal = Number(kpisGlobal.busquedas_activadas ?? 0);
  const pctBusqGlobal =
    totalGlobal > 0 ? ((busqGlobal / totalGlobal) * 100).toFixed(1) : "0";

  const asistentes =
    asistentesView === "ab" && activeGeo?.asistentesAb?.length
      ? activeGeo.asistentesAb
      : activeGeo?.asistentes || [];

  if (!activeGeo?.hasData && !asistentes.length && totalGlobal === 0) {
    return (
      <div className="geo-panel-card">
        <div className="geo-panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <span className="geo-badge-pill">GEO TRACKING T0</span>
            <h3 style={{ margin: "4px 0 0" }}>Visibilidad en Motores de Búsqueda IA</h3>
          </div>
          <button
            className="secondary-btn"
            style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", padding: "6px 12px" }}
            onClick={handleSync}
            disabled={isSyncing}
          >
            {isSyncing ? (
              <>
                <RefreshCw size={12} className="spin" />
                <span>Sincronizando...</span>
              </>
            ) : (
              "Sincronizar Google Sheet"
            )}
          </button>
        </div>
        {syncMsg && (
          <div style={{ marginTop: "12px", padding: "8px 12px", borderRadius: "6px", background: "rgba(75, 212, 160, 0.1)", border: "1px solid rgba(75, 212, 160, 0.2)", fontSize: "13px", color: "#4bd4a0" }}>
            {syncMsg}
          </div>
        )}
        <div style={{ margin: "20px 0", padding: "16px", background: "rgba(255,255,255,0.02)", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.06)" }}>
          {isDriveError ? (
            <div>
              <p style={{ color: "#f59e0b", fontWeight: 600, marginBottom: "8px" }}>
                Acceso a Google Drive requerido
              </p>
              <p style={{ fontSize: "13px", color: "var(--muted, #8e96a8)", lineHeight: 1.5, marginBottom: "12px" }}>
                Usa el botón de sincronización bajo demanda para actualizar la tabla nativa sin cuotas de Drive.
              </p>
            </div>
          ) : (
            <p className="chart-empty" style={{ margin: 0 }}>
              Sin datos de GEO Tracking sincronizados para este cliente.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Filtrado de runs
  const filteredRuns = runs.filter((r) => {
    const isAb =
      r.es_sin_marca === true ||
      r.grupo_pregunta === "A+B" ||
      String(r.prompt || "").startsWith("A -") ||
      String(r.prompt || "").startsWith("B -");
    if (runsFilter === "ab") return isAb;
    if (runsFilter === "other") return !isAb;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filteredRuns.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedRuns = filteredRuns.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="geo-panel-card" style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--radius)", padding: "20px", marginTop: "24px" }}>
      {/* Header */}
      <div className="geo-panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px", marginBottom: "16px" }}>
        <div className="geo-header-title">
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
            <span className="geo-badge-pill" style={{ background: "rgba(125, 150, 255, 0.12)", color: "var(--blue)", border: "1px solid rgba(125, 150, 255, 0.25)", fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "100px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              GOOGLE SHEETS · TRACKING T0
            </span>
            <span style={{ fontSize: "12px", color: "var(--muted)" }}>
              {totalGlobal} runs totales en base
            </span>
          </div>
          <h3 style={{ margin: "2px 0 6px", fontSize: "18px", fontWeight: 700 }}>
            GEO — Monitoreo de Respuestas y Posición en Motores de IA
          </h3>
          <p className="geo-header-subtitle" style={{ margin: 0, fontSize: "13px", color: "var(--muted)", maxWidth: "800px", lineHeight: 1.4 }}>
            Auditoría de presencia y recomendación de marca frente a consultas de usuarios en los principales motores generativos.
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <button
            className="secondary-btn"
            style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", padding: "6px 12px", cursor: "pointer", border: "1px solid var(--line-strong)", borderRadius: "var(--radius-sm)", background: "var(--surface-2)", color: "var(--text)" }}
            onClick={handleSync}
            disabled={isSyncing}
            title="Recargar tabla física BigQuery directamente desde el Google Sheet"
          >
            {isSyncing ? (
              <>
                <RefreshCw size={12} className="spin" />
                <span>Sincronizando...</span>
              </>
            ) : (
              "Sincronizar Sheet"
            )}
          </button>
          <button
            className="geo-toggle-btn"
            style={{ fontSize: "12px", padding: "6px 12px", cursor: "pointer", border: "1px solid var(--blue)", borderRadius: "var(--radius-sm)", background: showRuns ? "var(--blue)" : "transparent", color: showRuns ? "#fff" : "var(--blue)" }}
            onClick={() => setShowRuns((v) => !v)}
            title="Ver detalle fila a fila del Google Sheet"
          >
            {showRuns ? "Ocultar tabla de runs" : `Ver ${runs.length} runs evaluados`}
          </button>
        </div>
      </div>

      {syncMsg && (
        <div style={{ marginBottom: "16px", padding: "10px 14px", borderRadius: "8px", background: syncMsg.includes("Error") ? "rgba(255, 102, 94, 0.1)" : "rgba(75, 212, 160, 0.1)", border: `1px solid ${syncMsg.includes("Error") ? "rgba(255, 102, 94, 0.25)" : "rgba(75, 212, 160, 0.25)"}`, fontSize: "13px", color: syncMsg.includes("Error") ? "var(--red)" : "var(--green)" }}>
          {syncMsg}
        </div>
      )}

      {/* TARJETA PRINCIPAL DESTACADA: A+B (SIN MARCA NOMBRADA) */}
      <div style={{ background: "linear-gradient(135deg, rgba(85, 119, 255, 0.08) 0%, rgba(17, 20, 27, 0.95) 100%)", border: "1px solid rgba(125, 150, 255, 0.3)", borderRadius: "var(--radius)", padding: "18px 20px", marginBottom: "16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", flexWrap: "wrap", gap: "8px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ background: "var(--blue-strong)", color: "#fff", fontSize: "11px", fontWeight: 700, padding: "2px 8px", borderRadius: "6px", letterSpacing: "0.04em" }}>
              TARJETA PRINCIPAL · RECOMENDACIÓN ESPONTÁNEA
            </span>
            <h4 style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: "var(--text)" }}>
              Consultas A+B (Sin Nombrar la Marca)
            </h4>
          </div>
          <span style={{ fontSize: "12px", color: "var(--blue)", fontWeight: 600 }}>
            {totalAb} evaluaciones analizadas
          </span>
        </div>
        <p style={{ margin: "0 0 14px", fontSize: "12px", color: "var(--muted)", lineHeight: 1.4 }}>
          Prompts A (Lista/Ranking) y B (Conversacional) donde el usuario busca opciones generales sin inducir la marca. Representa el share de recomendación genuino frente a la competencia.
        </p>

        {/* 4 Métricas de A+B */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px" }}>
          <div style={{ background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", padding: "12px 14px" }}>
            <span style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: "4px" }}>
              Share de Mención Espontánea
            </span>
            <div style={{ fontSize: "24px", fontWeight: 800, color: pctMencionAb >= 25 ? "var(--green)" : pctMencionAb >= 10 ? "var(--yellow)" : "var(--red)" }}>
              {pctMencionAb}%
            </div>
            <span style={{ fontSize: "11px", color: "var(--dim)" }}>
              {mencionesAb} de {totalAb} respuestas
            </span>
          </div>

          <div style={{ background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", padding: "12px 14px" }}>
            <span style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: "4px" }}>
              Posición Media en Recomendación
            </span>
            <div style={{ fontSize: "24px", fontWeight: 800, color: "var(--text)" }}>
              {avgRankAb != null ? `#${avgRankAb}` : "—"}
            </div>
            <span style={{ fontSize: "11px", color: "var(--dim)" }}>
              Al ser recomendada de forma espontánea
            </span>
          </div>

          <div style={{ background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", padding: "12px 14px" }}>
            <span style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: "4px" }}>
              Citas con Enlace Verificable
            </span>
            <div style={{ fontSize: "24px", fontWeight: 800, color: "#38bdf8" }}>
              {linksAb}
            </div>
            <span style={{ fontSize: "11px", color: "var(--dim)" }}>
              Respuestas con link directo en A+B
            </span>
          </div>

          <div style={{ background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", padding: "12px 14px" }}>
            <span style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: "4px" }}>
              Búsqueda Web en Vivo
            </span>
            <div style={{ fontSize: "24px", fontWeight: 800, color: "#a78bfa" }}>
              {pctBusqAb}%
            </div>
            <span style={{ fontSize: "11px", color: "var(--dim)" }}>
              {busqAb} de {totalAb} activaron web search
            </span>
          </div>
        </div>
      </div>

      {/* SEGUNDO PLANO: OTRAS PREGUNTAS Y TOTAL CONSOLIDADO */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "12px", marginBottom: "20px" }}>
        {/* Otras Preguntas */}
        <div style={{ background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", padding: "14px 16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
            <span style={{ fontSize: "12px", fontWeight: 700, color: "var(--text)" }}>
              Otras Preguntas (Validación de Marca & Competidores)
            </span>
            <span style={{ fontSize: "11px", color: "var(--muted)" }}>
              {totalOtras} eval.
            </span>
          </div>
          <p style={{ margin: "0 0 10px", fontSize: "11px", color: "var(--dim)", lineHeight: 1.3 }}>
            Prompts de validación directa (D), comparativa con competidores (E) y utilitarios (G) donde la marca o el contexto fueron inducidos.
          </p>
          <div style={{ display: "flex", gap: "16px", alignItems: "baseline" }}>
            <div>
              <span style={{ fontSize: "10px", color: "var(--muted)", display: "block" }}>Mención inducida</span>
              <strong style={{ fontSize: "18px", color: "var(--text)" }}>{pctMencionOtras}%</strong>
              <span style={{ fontSize: "10px", color: "var(--dim)", marginLeft: "4px" }}>({mencionesOtras}/{totalOtras})</span>
            </div>
            <div>
              <span style={{ fontSize: "10px", color: "var(--muted)", display: "block" }}>Rank medio</span>
              <strong style={{ fontSize: "18px", color: "var(--text)" }}>{avgRankOtras != null ? `#${avgRankOtras}` : "—"}</strong>
            </div>
            <div>
              <span style={{ fontSize: "10px", color: "var(--muted)", display: "block" }}>Links citados</span>
              <strong style={{ fontSize: "18px", color: "#38bdf8" }}>{linksOtras}</strong>
            </div>
          </div>
        </div>

        {/* Total Consolidado */}
        <div style={{ background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", padding: "14px 16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
            <span style={{ fontSize: "12px", fontWeight: 700, color: "var(--text)" }}>
              Total Consolidado (Universo Completo)
            </span>
            <span style={{ fontSize: "11px", color: "var(--muted)" }}>
              {totalGlobal} eval.
            </span>
          </div>
          <p style={{ margin: "0 0 10px", fontSize: "11px", color: "var(--dim)", lineHeight: 1.3 }}>
            Suma global de los 554 runs de AFEX (133 sin marca + 421 otras preguntas) para referencia histórica y evolutiva.
          </p>
          <div style={{ display: "flex", gap: "16px", alignItems: "baseline" }}>
            <div>
              <span style={{ fontSize: "10px", color: "var(--muted)", display: "block" }}>Mención global</span>
              <strong style={{ fontSize: "18px", color: "var(--text)" }}>{pctMencionGlobal}%</strong>
              <span style={{ fontSize: "10px", color: "var(--dim)", marginLeft: "4px" }}>({mencionesGlobal}/{totalGlobal})</span>
            </div>
            <div>
              <span style={{ fontSize: "10px", color: "var(--muted)", display: "block" }}>Rank medio</span>
              <strong style={{ fontSize: "18px", color: "var(--text)" }}>{avgRankGlobal != null ? `#${avgRankGlobal}` : "—"}</strong>
            </div>
            <div>
              <span style={{ fontSize: "10px", color: "var(--muted)", display: "block" }}>Links totales</span>
              <strong style={{ fontSize: "18px", color: "#38bdf8" }}>{linksGlobal}</strong>
              <span style={{ fontSize: "10px", color: "var(--dim)", marginLeft: "4px" }}>({pctBusqGlobal}% web)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Desglose por Asistente de IA */}
      <div className="geo-asistentes-section" style={{ marginBottom: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", flexWrap: "wrap", gap: "8px" }}>
          <h4 style={{ margin: 0, fontSize: "14px", fontWeight: 700 }}>
            Desempeño Comparativo por Motor de IA
          </h4>
          <div style={{ display: "flex", gap: "4px", background: "var(--surface-3)", padding: "2px", borderRadius: "6px" }}>
            <button
              style={{ fontSize: "11px", padding: "4px 8px", border: "none", borderRadius: "4px", cursor: "pointer", background: asistentesView === "ab" ? "var(--blue)" : "transparent", color: asistentesView === "ab" ? "#fff" : "var(--muted)" }}
              onClick={() => setAsistentesView("ab")}
            >
              Solo A+B (Espontánea)
            </button>
            <button
              style={{ fontSize: "11px", padding: "4px 8px", border: "none", borderRadius: "4px", cursor: "pointer", background: asistentesView === "all" ? "var(--blue)" : "transparent", color: asistentesView === "all" ? "#fff" : "var(--muted)" }}
              onClick={() => setAsistentesView("all")}
            >
              Todas las Preguntas
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "10px" }}>
          {asistentes.map((asist, idx) => {
            const name = String(asist.asistente || "IA");
            const menc = Number(asist.menciones || 0);
            const tot = Number(asist.total || 0);
            const pct = Number(asist.pct_mencion || 0);
            const rank = asist.avg_rank != null ? Number(asist.avg_rank) : null;
            const links = Number(asist.links_verificables || 0);

            const colors: Record<string, string> = {
              Claude: "#d97706",
              GPT: "#10b981",
              Gemini: "#3b82f6",
              Perplexity: "#06b6d4",
              Copilot: "#8b5cf6",
            };
            const accent = colors[name] || "#6366f1";

            return (
              <div key={idx} style={{ background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", padding: "12px 14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: accent, display: "inline-block" }} />
                    <strong style={{ fontSize: "13px" }}>{name}</strong>
                  </div>
                  <span style={{ fontSize: "11px", color: "var(--muted)" }}>{tot} eval.</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "6px" }}>
                  <div style={{ fontSize: "18px", fontWeight: 700, color: pct >= 20 ? "var(--green)" : pct > 0 ? "var(--yellow)" : "var(--dim)" }}>
                    {pct}%
                  </div>
                  <span style={{ fontSize: "11px", color: "var(--dim)" }}>{menc} menciones</span>
                </div>
                <div style={{ height: "4px", background: "rgba(255,255,255,0.06)", borderRadius: "2px", overflow: "hidden", marginBottom: "8px" }}>
                  <div style={{ width: `${Math.min(pct, 100)}%`, height: "100%", background: accent }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: "var(--muted)" }}>
                  <span>Rank: <strong>{rank != null ? `#${rank}` : "—"}</strong></span>
                  <span>Links: <strong style={{ color: links > 0 ? "#38bdf8" : "inherit" }}>{links}</strong></span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tabla detallada de Runs (expandible) */}
      {showRuns && runs.length > 0 && (
        <div style={{ marginTop: "24px", paddingTop: "16px", borderTop: "1px solid var(--line)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", flexWrap: "wrap", gap: "8px" }}>
            <div>
              <h4 style={{ margin: "0 0 4px", fontSize: "15px", fontWeight: 700 }}>
                Detalle Fila a Fila de Respuestas Evaluadas
              </h4>
              <p style={{ margin: 0, fontSize: "12px", color: "var(--muted)" }}>
                Mostrando {pagedRuns.length} de {filteredRuns.length} runs ({runs.length} totales en base).
              </p>
            </div>

            <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
              <span style={{ fontSize: "11px", color: "var(--muted)" }}>Filtrar:</span>
              <button
                style={{ fontSize: "11px", padding: "4px 8px", borderRadius: "4px", border: "1px solid var(--line)", background: runsFilter === "all" ? "var(--blue)" : "var(--surface-2)", color: runsFilter === "all" ? "#fff" : "var(--text)", cursor: "pointer" }}
                onClick={() => { setRunsFilter("all"); setPage(1); }}
              >
                Todos ({runs.length})
              </button>
              <button
                style={{ fontSize: "11px", padding: "4px 8px", borderRadius: "4px", border: "1px solid var(--line)", background: runsFilter === "ab" ? "var(--blue)" : "var(--surface-2)", color: runsFilter === "ab" ? "#fff" : "var(--text)", cursor: "pointer" }}
                onClick={() => { setRunsFilter("ab"); setPage(1); }}
              >
                Solo A+B ({totalAb})
              </button>
              <button
                style={{ fontSize: "11px", padding: "4px 8px", borderRadius: "4px", border: "1px solid var(--line)", background: runsFilter === "other" ? "var(--blue)" : "var(--surface-2)", color: runsFilter === "other" ? "#fff" : "var(--text)", cursor: "pointer" }}
                onClick={() => { setRunsFilter("other"); setPage(1); }}
              >
                Otras ({totalOtras})
              </button>
            </div>
          </div>

          <div style={{ overflowX: "auto", maxHeight: "500px", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)" }}>
            <table className="usage-table detail-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
              <thead style={{ position: "sticky", top: 0, background: "var(--surface-3)", zIndex: 1 }}>
                <tr>
                  <th style={{ padding: "8px 10px", textAlign: "left" }}>ID</th>
                  <th style={{ padding: "8px 10px", textAlign: "left" }}>Fecha</th>
                  <th style={{ padding: "8px 10px", textAlign: "left" }}>Grupo</th>
                  <th style={{ padding: "8px 10px", textAlign: "left" }}>Motor</th>
                  <th style={{ padding: "8px 10px", textAlign: "left" }}>Intención / Prompt</th>
                  <th style={{ padding: "8px 10px", textAlign: "center" }}>¿Menciona?</th>
                  <th style={{ padding: "8px 10px", textAlign: "center" }}>Rank</th>
                  <th style={{ padding: "8px 10px", textAlign: "left" }}>Link Citado</th>
                  <th style={{ padding: "8px 10px", textAlign: "left" }}>Marcas Citadas</th>
                </tr>
              </thead>
              <tbody>
                {pagedRuns.map((r, i) => {
                  const menc = Boolean(r.cliente_mencionado);
                  const isAb =
                    r.es_sin_marca === true ||
                    r.grupo_pregunta === "A+B" ||
                    String(r.prompt || "").startsWith("A -") ||
                    String(r.prompt || "").startsWith("B -");

                  return (
                    <tr key={i} style={{ borderBottom: "1px solid var(--line)", background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)" }}>
                      <td style={{ padding: "8px 10px", color: "var(--dim)", fontFamily: "monospace" }}>#{String(r.run_id ?? "")}</td>
                      <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>{String(r.date || "—")}</td>
                      <td style={{ padding: "8px 10px" }}>
                        <span style={{ fontSize: "10px", fontWeight: 700, padding: "2px 6px", borderRadius: "4px", background: isAb ? "rgba(85, 119, 255, 0.2)" : "rgba(255, 255, 255, 0.05)", color: isAb ? "var(--blue)" : "var(--muted)" }}>
                          {isAb ? "A+B" : "OTRAS"}
                        </span>
                      </td>
                      <td style={{ padding: "8px 10px", fontWeight: 600 }}>{String(r.asistente || "—")}</td>
                      <td style={{ padding: "8px 10px", maxWidth: "260px" }}>
                        <div style={{ fontWeight: 600, color: "var(--text)" }}>{String(r.prompt || "—")}</div>
                        <div style={{ fontSize: "11px", color: "var(--muted)" }}>{String(r.intencion || "")}</div>
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "center" }}>
                        <span style={{ fontSize: "11px", fontWeight: 600, padding: "2px 6px", borderRadius: "4px", background: menc ? "rgba(75, 212, 160, 0.15)" : "rgba(255, 255, 255, 0.04)", color: menc ? "var(--green)" : "var(--dim)" }}>
                          {menc ? "✓ Sí" : "✗ No"}
                        </span>
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "center", fontWeight: 700 }}>
                        {r.cliente_rank != null ? `#${String(r.cliente_rank)}` : "—"}
                      </td>
                      <td style={{ padding: "8px 10px" }}>
                        {r.cliente_link_verificable ? (
                          <span style={{ color: "#38bdf8", fontWeight: 600 }}>✓ Link directo</span>
                        ) : (
                          <span style={{ color: "var(--dim)" }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: "8px 10px", fontSize: "11px", color: "var(--muted)", maxWidth: "220px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={String(r.marcas_en_orden || "")}>
                        {String(r.marcas_en_orden || "—")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px", fontSize: "12px", color: "var(--muted)" }}>
              <span>
                Página {currentPage} de {totalPages} ({filteredRuns.length} runs filtrados)
              </span>
              <div style={{ display: "flex", gap: "6px" }}>
                <button
                  disabled={currentPage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  style={{ padding: "4px 10px", borderRadius: "4px", border: "1px solid var(--line)", background: "var(--surface-2)", color: currentPage <= 1 ? "var(--dim)" : "var(--text)", cursor: currentPage <= 1 ? "not-allowed" : "pointer" }}
                >
                  Anterior
                </button>
                <button
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  style={{ padding: "4px 10px", borderRadius: "4px", border: "1px solid var(--line)", background: "var(--surface-2)", color: currentPage >= totalPages ? "var(--dim)" : "var(--text)", cursor: currentPage >= totalPages ? "not-allowed" : "pointer" }}
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
